import React, { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import apiClient from '@/lib/api/client';
import {
    Shield, Upload, FileCode2, CheckCircle2, XCircle, AlertTriangle,
    Info, ChevronDown, ChevronRight, RefreshCw, Download, Clock,
    BarChart3, ArrowLeft, Search, Filter, FileCheck, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Framework {
    id: string; name: string; version: string;
    description: string; category: string; rules_count: number;
}
interface FrameworkScore {
    framework_id: string; framework_name: string; compliance_score: number;
    total_rules: number; passed_rules: number; failed_rules: number;
    critical_findings: number; high_findings: number; medium_findings: number; low_findings: number;
}
interface Finding {
    finding_id: string; framework_id: string; rule_id: string; rule_name: string;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info'; category: string;
    status: string; description: string; file_path: string; line_number: number | null;
    code_snippet: string | null; remediation: string | null; confidence: number;
    source: string; created_at: string;
}
interface Audit {
    id: string; audit_id: string; project_id: number;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    framework_ids: string[]; frameworks_checked: string[];
    compliance_score: number; total_rules: number; passed_rules: number; failed_rules: number;
    framework_scores: FrameworkScore[]; violations: Finding[];
    summary: Record<string, unknown>; error_message: string | null;
    created_at: string; completed_at: string | null;
}
interface TestProject { id: number; name: string; }
interface AttachmentFile {
    id: number; title: string; original_filename: string;
    file_size: number; created_at: string;
}
interface SeverityCfg { color: string; bg: string; icon: React.ReactNode; }
interface StatusCfg { label: string; dot: string; cls: string; }
interface SummaryRow { label: string; value: number; cls: string; }
type Tab = 'configure' | 'history';
type CodeSource = 'upload' | 'existing';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_CFG: Record<string, SeverityCfg> = {
    critical: { color: 'text-red-700',    bg: 'bg-red-50 border-red-200',       icon: <XCircle className="h-3.5 w-3.5" /> },
    high:     { color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    medium:   { color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    low:      { color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200',     icon: <Info className="h-3.5 w-3.5" /> },
    info:     { color: 'text-gray-600',   bg: 'bg-gray-50 border-gray-200',     icon: <Info className="h-3.5 w-3.5" /> },
};
const STATUS_CFG: Record<string, StatusCfg> = {
    queued:     { label: 'Queued',     dot: 'bg-gray-400',               cls: 'bg-gray-100 text-gray-700 border-gray-300' },
    processing: { label: 'Processing', dot: 'bg-blue-500 animate-pulse', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
    completed:  { label: 'Completed',  dot: 'bg-green-500',              cls: 'bg-green-100 text-green-700 border-green-300' },
    failed:     { label: 'Failed',     dot: 'bg-red-500',                cls: 'bg-red-100 text-red-700 border-red-300' },
};
const CATEGORY_COLORS: Record<string, string> = {
    Financial: 'bg-emerald-100 text-emerald-800', Security: 'bg-blue-100 text-blue-800',
    Compliance: 'bg-purple-100 text-purple-800',  Government: 'bg-indigo-100 text-indigo-800',
    Healthcare: 'bg-red-100 text-red-800',        Privacy: 'bg-pink-100 text-pink-800',
    Defense: 'bg-slate-100 text-slate-800',       'Aviation/Safety': 'bg-sky-100 text-sky-800',
    Marketing: 'bg-orange-100 text-orange-800',   Transportation: 'bg-teal-100 text-teal-800',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const scoreColor = (s: number): string => s >= 80 ? 'text-green-600' : s >= 60 ? 'text-yellow-600' : 'text-red-600';
const fmtDate = (iso: string | null): string => iso ? new Date(iso).toLocaleString() : '—';
const fmtPct = (n: number): string => `${Math.round(n)}%`;
const isActiveStatus = (s: string): boolean => s === 'queued' || s === 'processing';
const apiError = (err: unknown): string =>
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'An error occurred';

// ---------------------------------------------------------------------------
// Primitive components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }): React.ReactElement {
    const cfg: StatusCfg = STATUS_CFG[status] ?? STATUS_CFG.queued;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
    </span>
    );
}

function SeverityBadge({ severity }: { severity: string }): React.ReactElement {
    const cfg: SeverityCfg = SEVERITY_CFG[severity] ?? SEVERITY_CFG.info;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {cfg.icon}{severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
    );
}

function ScoreGauge({ score }: { score: number }): React.ReactElement {
    const pct = Math.min(100, Math.max(0, score));
    const dash = (pct / 100) * 251.2;
    const stroke = pct >= 80 ? '#22c55e' : pct >= 60 ? '#eab308' : '#ef4444';
    return (
        <div className="flex flex-col items-center gap-1">
            <div className="relative h-24 w-24">
                <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke={stroke} strokeWidth="10"
                            strokeDasharray={`${dash} 251.2`} strokeLinecap="round" className="transition-all duration-700" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-2xl font-bold ${scoreColor(pct)}`}>{Math.round(pct)}</span>
                    <span className="text-[10px] text-gray-400">/ 100</span>
                </div>
            </div>
            <p className="text-xs text-gray-500">Compliance Score</p>
        </div>
    );
}

function ProgressBar({ value }: { value: number }): React.ReactElement {
    const pct = Math.min(100, Math.max(0, value));
    const bg = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
    return (
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div className={`h-full rounded-full transition-all duration-500 ${bg}`} style={{ width: `${pct}%` }} />
        </div>
    );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }): React.ReactElement {
    return (
        <button type="button" onClick={() => onChange(!checked)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}>
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
        </button>
    );
}

function FindingRow({ finding }: { finding: Finding }): React.ReactElement {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow className="cursor-pointer hover:bg-gray-50" onClick={() => setOpen(p => !p)}>
                <TableCell>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                <TableCell><SeverityBadge severity={finding.severity} /></TableCell>
                <TableCell className="font-mono text-xs">{finding.rule_id}</TableCell>
                <TableCell className="max-w-xs truncate text-sm" title={finding.description}>{finding.description}</TableCell>
                <TableCell className="font-mono text-xs text-gray-500">
                    {finding.file_path}{finding.line_number != null ? `:${finding.line_number}` : ''}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{finding.framework_id}</Badge></TableCell>
                <TableCell>
          <span className={`text-xs font-medium capitalize ${finding.status === 'open' ? 'text-red-600' : 'text-gray-500'}`}>
            {finding.status}
          </span>
                </TableCell>
            </TableRow>
            {open && (
                <TableRow>
                    <TableCell colSpan={7} className="bg-gray-50 p-4">
                        <div className="space-y-3 text-sm">
                            <div><p className="font-semibold">Description</p><p className="text-gray-600">{finding.description}</p></div>
                            {finding.code_snippet && (
                                <div>
                                    <p className="font-semibold">Code Snippet</p>
                                    <pre className="mt-1 overflow-x-auto rounded bg-gray-900 p-3 text-xs text-green-400">{finding.code_snippet}</pre>
                                </div>
                            )}
                            {finding.remediation && (
                                <div><p className="font-semibold">Remediation</p><p className="whitespace-pre-wrap text-gray-600">{finding.remediation}</p></div>
                            )}
                            <div className="flex gap-4 text-xs text-gray-400">
                                <span>Source: <strong>{finding.source}</strong></span>
                                <span>Confidence: <strong>{Math.round(finding.confidence * 100)}%</strong></span>
                                {finding.category && <span>Category: <strong>{finding.category}</strong></span>}
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// AuditDetail
// ---------------------------------------------------------------------------

function AuditDetail({ audit, onBack, onRefresh }: {
    audit: Audit; onBack: () => void; onRefresh: () => void;
}): React.ReactElement {
    const [sevFilter, setSevFilter] = useState('all');
    const [fwFilter, setFwFilter] = useState('all');
    const [search, setSearch] = useState('');
    const terminal = audit.status === 'completed' || audit.status === 'failed';

    const findings = (audit.violations ?? []).filter(f => {
        if (sevFilter !== 'all' && f.severity !== sevFilter) return false;
        if (fwFilter !== 'all' && f.framework_id !== fwFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return f.description.toLowerCase().includes(q) || f.rule_id.toLowerCase().includes(q) || f.file_path.toLowerCase().includes(q);
        }
        return true;
    });

    const sevCounts = (audit.violations ?? []).reduce<Record<string, number>>((acc, f) => {
        acc[f.severity] = (acc[f.severity] ?? 0) + 1; return acc;
    }, {});

    const fwOptions = [...new Set((audit.violations ?? []).map(f => f.framework_id))];

    const downloadReport = async (fmt: 'pdf' | 'json'): Promise<void> => {
        try {
            const res = await apiClient.post(
                `/api/compliance-audit/audits/${audit.audit_id}/report`,
                { format: fmt },
                { responseType: 'blob' },
            );
            const url = URL.createObjectURL(res.data as Blob);
            const a = document.createElement('a');
            a.href = url; a.download = `compliance_audit_${audit.audit_id}.${fmt}`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Report not available for this audit'); }
    };

    const summaryRows: SummaryRow[] = [
        { label: 'Total Rules', value: audit.total_rules,  cls: '' },
        { label: 'Passed',      value: audit.passed_rules, cls: 'text-green-600' },
        { label: 'Failed',      value: audit.failed_rules, cls: 'text-red-600' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
                    <div className="h-5 w-px bg-gray-200" />
                    <div>
                        <h2 className="text-lg font-semibold">Audit Results</h2>
                        <p className="font-mono text-xs text-gray-400">{audit.audit_id}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!terminal && (
                        <Button variant="outline" size="sm" onClick={onRefresh}>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />Refresh
                        </Button>
                    )}
                    {audit.status === 'completed' && (
                        <>
                            <Button variant="outline" size="sm" onClick={() => void downloadReport('json')}><Download className="mr-2 h-4 w-4" />JSON</Button>
                            <Button variant="outline" size="sm" onClick={() => void downloadReport('pdf')}><Download className="mr-2 h-4 w-4" />PDF</Button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card><CardContent className="flex justify-center pt-6"><ScoreGauge score={audit.compliance_score} /></CardContent></Card>
                <Card>
                    <CardContent className="space-y-3 pt-6">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Status</span><StatusBadge status={audit.status} />
                        </div>
                        <div className="h-px bg-gray-100" />
                        <div className="space-y-1 text-sm">
                            {summaryRows.map(row => (
                                <div key={row.label} className="flex justify-between">
                                    <span className="text-gray-500">{row.label}</span>
                                    <span className={`font-medium ${row.cls}`}>{row.value}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="space-y-2 pt-6">
                        <p className="text-sm font-medium">Findings by Severity</p>
                        {(['critical', 'high', 'medium', 'low'] as const).map(sev => (
                            <div key={sev} className="flex items-center gap-2">
                                <SeverityBadge severity={sev} />
                                <span className="ml-auto font-mono text-sm font-semibold">{sevCounts[sev] ?? 0}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="space-y-2 pt-6">
                        <p className="text-sm font-medium">Frameworks</p>
                        <div className="flex flex-wrap gap-1">
                            {(audit.framework_ids ?? []).map(fw => <Badge key={fw} variant="outline" className="text-xs">{fw}</Badge>)}
                        </div>
                        <div className="h-px bg-gray-100" />
                        <div className="space-y-0.5 text-xs text-gray-400">
                            <div>Started: {fmtDate(audit.created_at)}</div>
                            {audit.completed_at && <div>Completed: {fmtDate(audit.completed_at)}</div>}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {audit.status === 'failed' && audit.error_message && (
                <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{audit.error_message}</span>
                </div>
            )}

            {(audit.framework_scores ?? []).length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4" />Framework Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {audit.framework_scores.map(fs => (
                            <div key={fs.framework_id} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="font-medium">{fs.framework_name}</span>
                                    <span className={`font-semibold ${scoreColor(fs.compliance_score)}`}>{fmtPct(fs.compliance_score)}</span>
                                </div>
                                <ProgressBar value={fs.compliance_score} />
                                <div className="flex gap-3 text-xs text-gray-400">
                                    <span>{fs.passed_rules}/{fs.total_rules} rules passed</span>
                                    {fs.critical_findings > 0 && <span className="text-red-600">{fs.critical_findings} critical</span>}
                                    {fs.high_findings > 0 && <span className="text-orange-600">{fs.high_findings} high</span>}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {(audit.violations ?? []).length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base"><FileCheck className="h-4 w-4" />Findings ({findings.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                                <Input placeholder="Search…" className="h-8 w-56 pl-8 text-sm" value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                            <Select value={sevFilter} onValueChange={setSevFilter}>
                                <SelectTrigger className="h-8 w-40 text-sm"><Filter className="mr-1.5 h-3 w-3" /><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Severities</SelectItem>
                                    <SelectItem value="critical">Critical</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="low">Low</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={fwFilter} onValueChange={setFwFilter}>
                                <SelectTrigger className="h-8 w-40 text-sm"><SelectValue placeholder="Framework" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Frameworks</SelectItem>
                                    {fwOptions.map(fw => <SelectItem key={fw} value={fw}>{fw}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="overflow-hidden rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-8" /><TableHead>Severity</TableHead><TableHead>Rule</TableHead>
                                        <TableHead>Description</TableHead><TableHead>Location</TableHead>
                                        <TableHead>Framework</TableHead><TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {findings.length === 0 ? (
                                        <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-gray-400">No findings match the current filters.</TableCell></TableRow>
                                    ) : (
                                        findings.map(f => <FindingRow key={f.finding_id} finding={f} />)
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {audit.status === 'completed' && (audit.violations ?? []).length === 0 && (
                <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                    <CheckCircle2 className="h-6 w-6" />
                    <div><p className="font-semibold">All checks passed</p><p className="text-sm text-green-700">No compliance violations detected.</p></div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// CompliancePage
// ---------------------------------------------------------------------------

export default function CompliancePage(): React.ReactElement {
    const qc = useQueryClient();
    const [tab, setTab] = useState<Tab>('configure');
    const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
    const [projectId, setProjectId] = useState('');
    const [projectName, setProjectName] = useState('');
    const [selectedFrameworks, setSelectedFrameworks] = useState<Set<string>>(new Set());
    const [codeSource, setCodeSource] = useState<CodeSource>('upload');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [existingAttachmentId, setExistingAttachmentId] = useState<number | null>(null);
    const [severityThreshold, setSeverityThreshold] = useState('medium');
    const [includeEvidence, setIncludeEvidence] = useState(true);
    const [includeRecommendations, setIncludeRecommendations] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [historyProject, setHistoryProject] = useState('all-projects');
    const [historyStatus, setHistoryStatus] = useState('all');

    const { data: frameworksData } = useQuery({
        queryKey: ['compliance-frameworks'],
        queryFn: async () => {
            const res = await apiClient.get<{ frameworks: Framework[]; count: number }>('/api/compliance-audit/frameworks');
            return res.data;
        },
        staleTime: 1000 * 60 * 10,
    });

    const { data: projectsData } = useQuery({
        queryKey: ['test-projects-list'],
        queryFn: async () => {
            const res = await apiClient.get<{ projects?: TestProject[] } | TestProject[]>('/api/projects?limit=100');
            const d = res.data;
            return (Array.isArray(d) ? d : (d as { projects?: TestProject[] }).projects ?? []) as TestProject[];
        },
        staleTime: 1000 * 60 * 5,
    });

    const { data: existingFilesData, isFetching: fetchingFiles } = useQuery({
        queryKey: ['compliance-attachments', projectId],
        queryFn: async () => {
            const res = await apiClient.get<{ attachments?: AttachmentFile[] } | AttachmentFile[]>(
                `/api/attachments?fk_table=compliance_scan&fk_id=${projectId}&limit=50`,
            );
            const d = res.data;
            return (Array.isArray(d) ? d : (d as { attachments?: AttachmentFile[] }).attachments ?? []) as AttachmentFile[];
        },
        enabled: codeSource === 'existing' && !!projectId,
        staleTime: 1000 * 30,
    });

    const existingFiles = existingFilesData ?? [];

    const { data: auditsData, refetch: refetchAudits } = useQuery({
        queryKey: ['compliance-audits', historyProject, historyStatus],
        queryFn: async () => {
            const params: Record<string, string | number> = { limit: 50, offset: 0 };
            if (historyProject !== 'all-projects') params.project_id = Number(historyProject);
            if (historyStatus !== 'all') params.status = historyStatus;
            const res = await apiClient.get<{ audits: Audit[]; count: number }>('/api/compliance-audit/audits', { params });
            return res.data;
        },
        enabled: tab === 'history',
        refetchInterval: (query) => {
            const data = query.state.data;
            if (!data) return false;
            return data.audits.some(a => isActiveStatus(a.status)) ? 5000 : false;
        },
    });

    const { data: auditDetail, refetch: refetchSelected } = useQuery({
        queryKey: ['compliance-audit-detail', selectedAudit?.audit_id],
        queryFn: async () => {
            const res = await apiClient.get<Audit>(`/api/compliance-audit/audits/${selectedAudit!.audit_id}`);
            return res.data;
        },
        enabled: !!selectedAudit,
        refetchInterval: (query) => {
            const data = query.state.data;
            if (!data) return false;
            return isActiveStatus(data.status) ? 4000 : false;
        },
    });

    const uploadMutation = useMutation({
        mutationFn: async (file: File): Promise<number> => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('fk_table', 'compliance_scan');
            fd.append('fk_id', projectId || '0');
            fd.append('title', `Compliance Codeset — ${file.name}`);
            const res = await apiClient.post<{ id?: number; attachment_id?: number }>(
                '/api/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            return (res.data.id ?? res.data.attachment_id) as number;
        },
    });

    const createAuditMutation = useMutation({
        mutationFn: async (attachmentId: number): Promise<Audit> => {
            const proj = (projectsData ?? []).find(p => String(p.id) === projectId);
            const res = await apiClient.post<Audit>('/api/compliance-audit/audits', {
                project_id: Number(projectId),
                project_name: proj?.name ?? (projectName || `Project ${projectId}`),
                framework_ids: [...selectedFrameworks],
                attachment_id: attachmentId,
                severity_threshold: severityThreshold,
                include_evidence: includeEvidence,
                include_recommendations: includeRecommendations,
            });
            return res.data;
        },
        onSuccess: (audit) => {
            toast.success('Compliance audit submitted', { description: `Queued for ${selectedFrameworks.size} framework(s).` });
            setSelectedAudit(audit);
            void qc.invalidateQueries({ queryKey: ['compliance-audits'] });
        },
        onError: (err: unknown) => {
            toast.error('Submission failed', { description: apiError(err) });
        },
    });

    const isSubmitting = uploadMutation.isPending || createAuditMutation.isPending;

    const handleSubmit = async (): Promise<void> => {
        if (!projectId) { toast.error('Select a project'); return; }
        if (selectedFrameworks.size === 0) { toast.error('Select at least one framework'); return; }
        let attachmentId: number;
        if (codeSource === 'upload') {
            if (!uploadedFile) { toast.error('Upload a code archive'); return; }
            try {
                attachmentId = await uploadMutation.mutateAsync(uploadedFile);
            } catch (err: unknown) {
                toast.error('Upload failed', { description: apiError(err) });
                return;
            }
        } else {
            if (!existingAttachmentId) { toast.error('Select a previously uploaded codeset'); return; }
            attachmentId = existingAttachmentId;
        }
        createAuditMutation.mutate(attachmentId);
    };

    const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) setUploadedFile(file);
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (file) setUploadedFile(file);
    };

    const toggleFramework = (id: string): void => {
        setSelectedFrameworks(prev => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
    };

    const frameworks = frameworksData?.frameworks ?? [];
    const categories = ['all', ...Array.from(new Set(frameworks.map(f => f.category)))];
    const visibleFrameworks = categoryFilter === 'all' ? frameworks : frameworks.filter(f => f.category === categoryFilter);

    if (selectedAudit) {
        return (
            <div className="mx-auto max-w-7xl p-6">
                <AuditDetail
                    audit={auditDetail ?? selectedAudit}
                    onBack={() => setSelectedAudit(null)}
                    onRefresh={() => void refetchSelected()}
                />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl space-y-6 p-6">
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                    <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Compliance Audit</h1>
                    <p className="text-sm text-gray-500">Analyze code against PCI DSS, HIPAA, GDPR, SOC 2, and more</p>
                </div>
            </div>

            <div className="flex gap-1 border-b">
                {(['configure', 'history'] as Tab[]).map(t => (
                    <button key={t} type="button" onClick={() => setTab(t)}
                            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        {t === 'configure' ? 'Configure Audit' : 'Audit History'}
                    </button>
                ))}
            </div>

            {tab === 'configure' && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="space-y-5">
                        <Card>
                            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Project</CardTitle></CardHeader>
                            <CardContent>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Select Project *</Label>
                                    <Select value={projectId} onValueChange={v => {
                                        setProjectId(v);
                                        setExistingAttachmentId(null);
                                        const p = (projectsData ?? []).find(pr => String(pr.id) === v);
                                        if (p) setProjectName(p.name);
                                    }}>
                                        <SelectTrigger className="text-sm"><SelectValue placeholder="Choose a project…" /></SelectTrigger>
                                        <SelectContent>
                                            {(projectsData ?? []).map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-semibold">Code Source</CardTitle>
                                <CardDescription className="text-xs">Upload a new archive or select a previously uploaded codeset</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex gap-1 rounded-lg border p-1">
                                    {(['upload', 'existing'] as CodeSource[]).map(src => (
                                        <button key={src} type="button"
                                                onClick={() => {
                                                    setCodeSource(src);
                                                    if (src === 'upload') setExistingAttachmentId(null);
                                                    if (src === 'existing') setUploadedFile(null);
                                                }}
                                                className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${codeSource === src ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                            {src === 'upload' ? 'Upload New' : 'Use Existing'}
                                        </button>
                                    ))}
                                </div>

                                {codeSource === 'upload' ? (
                                    <div
                                        onDrop={handleFileDrop}
                                        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                                        onDragLeave={() => setIsDragging(false)}
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                                            isDragging ? 'border-blue-500 bg-blue-50' : uploadedFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-400'
                                        }`}
                                    >
                                        <input ref={fileInputRef} type="file" accept=".zip,.tar.gz,.tar,.tgz,.gz" className="hidden" onChange={handleFileSelect} />
                                        {uploadedFile ? (
                                            <>
                                                <FileCode2 className="mb-2 h-8 w-8 text-green-600" />
                                                <p className="text-sm font-medium text-green-800">{uploadedFile.name}</p>
                                                <p className="mt-0.5 text-xs text-green-600">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB — click to replace</p>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="mb-2 h-8 w-8 text-gray-400" />
                                                <p className="text-sm font-medium text-gray-700">Drop archive here or click to browse</p>
                                                <p className="mt-0.5 text-xs text-gray-400">.zip, .tar.gz, .tgz</p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {!projectId ? (
                                            <p className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                                                Select a project first to see previously uploaded codesets.
                                            </p>
                                        ) : fetchingFiles ? (
                                            <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-400">
                                                <Loader2 className="h-4 w-4 animate-spin" />Loading files…
                                            </div>
                                        ) : existingFiles.length === 0 ? (
                                            <p className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                                                No previously uploaded codesets found for this project.
                                            </p>
                                        ) : (
                                            <div className="max-h-48 space-y-1.5 overflow-y-auto">
                                                {existingFiles.map(f => (
                                                    <button key={f.id} type="button" onClick={() => setExistingAttachmentId(f.id)}
                                                            className={`w-full rounded-lg border p-2.5 text-left transition-colors ${existingAttachmentId === f.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-400'}`}>
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="min-w-0">
                                                                <p className="truncate text-xs font-medium">{f.title || f.original_filename}</p>
                                                                <p className="text-[10px] text-gray-400">{f.original_filename} · {(f.file_size / 1024 / 1024).toFixed(2)} MB · {fmtDate(f.created_at)}</p>
                                                            </div>
                                                            {existingAttachmentId === f.id && <CheckCircle2 className="h-4 w-4 shrink-0 text-blue-500" />}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Scan Options</CardTitle></CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Severity Threshold</Label>
                                    <Select value={severityThreshold} onValueChange={setSeverityThreshold}>
                                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="critical">Critical only</SelectItem>
                                            <SelectItem value="high">High and above</SelectItem>
                                            <SelectItem value="medium">Medium and above</SelectItem>
                                            <SelectItem value="low">All findings</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Include Evidence</Label>
                                    <Toggle checked={includeEvidence} onChange={setIncludeEvidence} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs">Include Recommendations</Label>
                                    <Toggle checked={includeRecommendations} onChange={setIncludeRecommendations} />
                                </div>
                            </CardContent>
                        </Card>

                        <Button className="w-full" size="lg" onClick={() => void handleSubmit()} disabled={isSubmitting || !projectId || selectedFrameworks.size === 0}>
                            {isSubmitting ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{uploadMutation.isPending ? 'Uploading…' : 'Submitting…'}</>
                            ) : (
                                <><Shield className="mr-2 h-4 w-4" />Run Compliance Audit{selectedFrameworks.size > 0 && ` (${selectedFrameworks.size})`}</>
                            )}
                        </Button>
                    </div>

                    <div className="space-y-4 lg:col-span-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold">Select Frameworks</h2>
                                <p className="text-xs text-gray-500">{selectedFrameworks.size} selected — {frameworks.length} available</p>
                            </div>
                            <div className="flex gap-2 text-xs">
                                <button type="button" className="text-blue-600 hover:underline" onClick={() => setSelectedFrameworks(new Set(frameworks.map(f => f.id)))}>Select All</button>
                                <span className="text-gray-300">|</span>
                                <button type="button" className="text-blue-600 hover:underline" onClick={() => setSelectedFrameworks(new Set())}>Clear</button>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                            {categories.map(cat => (
                                <button key={cat} type="button" onClick={() => setCategoryFilter(cat)}
                                        className={`rounded-full border px-3 py-0.5 text-xs font-medium transition-colors ${categoryFilter === cat ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
                                    {cat === 'all' ? 'All' : cat}
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {visibleFrameworks.map(fw => {
                                const sel = selectedFrameworks.has(fw.id);
                                return (
                                    <button key={fw.id} type="button" onClick={() => toggleFramework(fw.id)}
                                            className={`rounded-lg border p-3 text-left transition-all ${sel ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 bg-white hover:border-gray-400'}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="mb-1 flex items-center gap-1.5">
                                                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${CATEGORY_COLORS[fw.category] ?? 'bg-gray-100 text-gray-700'}`}>{fw.category}</span>
                                                    <span className="text-[10px] text-gray-400">{fw.rules_count} rules</span>
                                                </div>
                                                <p className="text-xs font-semibold leading-tight">{fw.name}</p>
                                                <p className="mt-0.5 truncate text-[11px] text-gray-400" title={fw.description}>{fw.description}</p>
                                            </div>
                                            <div className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${sel ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                                {sel && <CheckCircle2 className="h-3 w-3 text-white" />}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {tab === 'history' && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex gap-2">
                            <Select value={historyProject} onValueChange={setHistoryProject}>
                                <SelectTrigger className="h-8 w-48 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all-projects">All Projects</SelectItem>
                                    {(projectsData ?? []).map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={historyStatus} onValueChange={setHistoryStatus}>
                                <SelectTrigger className="h-8 w-36 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="queued">Queued</SelectItem>
                                    <SelectItem value="processing">Processing</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => void refetchAudits()}>
                            <RefreshCw className="mr-2 h-3.5 w-3.5" />Refresh
                        </Button>
                    </div>

                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Audit ID</TableHead>
                                    <TableHead>Project</TableHead>
                                    <TableHead>Frameworks</TableHead>
                                    <TableHead>Score</TableHead>
                                    <TableHead>Rules</TableHead>
                                    <TableHead><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Created</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(auditsData?.audits ?? []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="py-12 text-center text-sm text-gray-400">
                                            No audits found. Configure and run your first audit.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    (auditsData?.audits ?? []).map(a => (
                                        <TableRow key={a.audit_id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedAudit(a)}>
                                            <TableCell><StatusBadge status={a.status} /></TableCell>
                                            <TableCell className="font-mono text-xs text-gray-400">{a.audit_id.slice(0, 8)}…</TableCell>
                                            <TableCell className="text-sm">Project {a.project_id}</TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1">
                                                    {(a.framework_ids ?? []).slice(0, 3).map(fw => <Badge key={fw} variant="outline" className="text-[10px]">{fw}</Badge>)}
                                                    {(a.framework_ids ?? []).length > 3 && <Badge variant="outline" className="text-[10px]">+{a.framework_ids.length - 3}</Badge>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {a.status === 'completed'
                                                    ? <span className={`font-semibold ${scoreColor(a.compliance_score)}`}>{fmtPct(a.compliance_score)}</span>
                                                    : <span className="text-sm text-gray-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {a.status === 'completed'
                                                    ? <><span className="text-green-600">{a.passed_rules}</span><span className="text-gray-400">/{a.total_rules}</span></>
                                                    : '—'}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap text-xs text-gray-400">{fmtDate(a.created_at)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </div>
            )}
        </div>
    );
}